import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BatchExecutionLog, DTJExecutionLog, ManualBatchItem, SysManagementService, TSReminderExecutionLog } from '../../services/sys-management.service';

@Component({
  selector: 'app-sys-batch',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './sys-batch.html',
  styleUrl: './sys-batch.scss'
})
export class SysBatchComponent implements OnInit {
  // バッチ稼働状況のプロパティ
  selectedDate: string = '';
  dateOptions: string[] = [];
  selectedResultType: 'all' | 'success' | 'failure' = 'all';
  batchExecutionLogs: BatchExecutionLog[] = [];
  isLoading: boolean = false;

  // DTJExport処理状況のプロパティ
  dtjSelectedDate: string = '';
  dtjDateOptions: string[] = [];
  dtjExecutionLogs: DTJExecutionLog[] = [];
  dtjIsLoading: boolean = false;

  // TSReminder処理状況のプロパティ
  tsrExecutionLogs: TSReminderExecutionLog[] = [];
  tsrIsLoading: boolean = false;

  // バッチ手動実行のプロパティ
  manualBatchList: ManualBatchItem[] = [];
  selectedBatchName: string = '';
  isManualExecuting: boolean = false;
  manualExecutionMessage: string = '';
  manualExecutionSuccess: boolean | null = null;

  constructor(private sysManagementService: SysManagementService) { }

  ngOnInit(): void {
    this.initializeDateOptions();
    this.initializeDTJDateOptions();
    this.loadBatchExecutionLogs();
    this.loadDTJExecutionLogs();
    this.loadTSReminderExecutionLogs();
    this.loadManualBatchList();
  }

  // 日付オプションを初期化（今日を含む過去31日間）
  private initializeDateOptions(): void {
    const today = new Date();
    this.selectedDate = this.formatDate(today);

    for (let i = 0; i < 31; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      this.dateOptions.push(this.formatDate(date));
    }
  }

  // 日付をYYYY-MM-DD形式にフォーマット
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // 日付を表示用にフォーマット（YYYY/MM/DD）
  getDisplayDate(dateStr: string): string {
    return dateStr.replace(/-/g, '/');
  }

  // バッチ実行ログを取得
  loadBatchExecutionLogs(): void {
    this.isLoading = true;
    this.sysManagementService.getBatchExecutionLogs(this.selectedDate, this.selectedResultType)
      .subscribe({
        next: (logs) => {
          this.batchExecutionLogs = logs;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.batchExecutionLogs = [];
        }
      });
  }

  // 再抽出ボタンクリック
  onRefreshClick(): void {
    this.loadBatchExecutionLogs();
  }

  // 日付変更時
  onDateChange(): void {
    // 再抽出ボタンが押されるまで待つ
  }

  // 種類変更時
  onResultTypeChange(): void {
    // 再抽出ボタンが押されるまで待つ
  }

  // DTJExport用: 日付オプションを初期化
  private initializeDTJDateOptions(): void {
    const today = new Date();
    this.dtjSelectedDate = this.formatDate(today);

    this.dtjDateOptions = [];
    for (let i = 0; i < 31; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      this.dtjDateOptions.push(this.formatDate(date));
    }
  }

  // DTJExport実行ログを取得
  loadDTJExecutionLogs(): void {
    this.dtjIsLoading = true;
    this.sysManagementService.getDTJExecutionLogs(this.dtjSelectedDate)
      .subscribe({
        next: (logs) => {
          this.dtjExecutionLogs = logs;
          this.dtjIsLoading = false;
        },
        error: () => {
          this.dtjIsLoading = false;
          this.dtjExecutionLogs = [];
        }
      });
  }

  // DTJExport用: 再抽出ボタンクリック
  onDTJRefreshClick(): void {
    this.loadDTJExecutionLogs();
  }

  // DTJExport用: 日付変更時
  onDTJDateChange(): void {
    // 再抽出ボタンが押されるまで待つ
  }

  // TSReminder実行ログを取得
  loadTSReminderExecutionLogs(): void {
    this.tsrIsLoading = true;
    this.sysManagementService.getTSReminderExecutionLogs()
      .subscribe({
        next: (logs) => {
          this.tsrExecutionLogs = logs;
          this.tsrIsLoading = false;
        },
        error: () => {
          this.tsrIsLoading = false;
          this.tsrExecutionLogs = [];
        }
      });
  }

  // 手動実行可能バッチ一覧を取得
  loadManualBatchList(): void {
    this.sysManagementService.getManualBatchList().subscribe({
      next: (list) => { this.manualBatchList = list; },
      error: ()     => { this.manualBatchList = []; }
    });
  }

  // 手動実行ボタンクリック
  onManualExecuteClick(): void {
    if (!this.selectedBatchName) return;

    const selected = this.manualBatchList.find(b => b.batchName === this.selectedBatchName);
    const displayName = selected?.jobName ?? this.selectedBatchName;

    if (!confirm(`「${displayName}」を手動実行してもよろしいですか？`)) return;

    this.isManualExecuting = true;
    this.manualExecutionMessage = '';
    this.manualExecutionSuccess = null;

    this.sysManagementService.executeBatch(this.selectedBatchName).subscribe({
      next: (result) => {
        this.isManualExecuting = false;
        this.manualExecutionSuccess = result.success;
        this.manualExecutionMessage = result.message;
      },
      error: (err) => {
        this.isManualExecuting = false;
        this.manualExecutionSuccess = false;
        this.manualExecutionMessage = err?.error?.message ?? 'バッチの実行中にエラーが発生しました。';
      }
    });
  }
}
